import { TFunction } from 'i18next';
import * as Yup from 'yup';

import {
  yupMaxField,
  yupMinField,
  yupRequiredField,
} from '../../../../../../utils/helpers/yup';
import { SortingMachinesType } from '../../../SortingMachine.functions';
import { SortingMapsType } from '../../SortingMaps.functions';

export type CreatePorts = {
  logisticsPointId: number;
  pointTypeId?: number;
  pointName?: string;
  port: number;
};

export type Ports = {
  id: number;
  sortMapId: number;
  logisticsPointId: number;
  pointName?: string;
  port: number;
};

export type CreateSortingMapsType = {
  name: string;
  description: string;
  errorPort: number;
  unmatchedPort: number;
  ports: CreatePorts[];
};

export function getDefaultValues(
  isEditDialog: boolean,
  sortingMap: SortingMapsType | undefined,
  ports: any[] | undefined
): CreateSortingMapsType {
  return {
    name: isEditDialog ? sortingMap?.name ?? '' : '',
    description: isEditDialog ? sortingMap?.description ?? '' : '',
    errorPort: isEditDialog ? sortingMap?.errorPort ?? 1 : 1,
    unmatchedPort: isEditDialog ? sortingMap?.unmatchedPort ?? 2 : 2,
    ports: isEditDialog
      ? ports && ports.length > 0
        ? // Sorted by port so points sharing one sit next to each other - the api returns them in
          // id order, which scatters a grouped port across the table.
          ports
            .map((item) => ({
              pointTypeId: item.logisticsPointTypeId,
              pointName: item.logisticsPointName ?? '',
              logisticsPointId: item.logisticsPointId,
              port: item.port,
            }))
            .sort(
              (a, b) =>
                a.port - b.port || a.pointName.localeCompare(b.pointName)
            )
        : []
      : [
          {
            pointTypeId: 1,
            pointName: '',
            logisticsPointId: 0,
            port: 3,
          },
        ],
  };
}

export function getValidationSchema(t: TFunction, data?: SortingMachinesType) {
  return Yup.object().shape({
    name: Yup.string()
      .required(yupRequiredField(t, t('Name')))
      .min(2, ({ min }) => yupMinField(t, t('Name'), min))
      .max(50, ({ max }) => yupMaxField(t, t('Name'), max)),
    description: Yup.string()
      .required(yupRequiredField(t, t('Description')))
      .min(2, ({ min }) => yupMinField(t, t('Description'), min))
      .max(100, ({ max }) => yupMaxField(t, t('Description'), max)),
    errorPort: Yup.number()
      .required(yupRequiredField(t, t('Error Port')))
      .min(0, ({ min }) => yupMinField(t, t('Error Port'), min)),
    unmatchedPort: Yup.number()
      .required(yupRequiredField(t, t('Unmatched Port')))
      .min(0, ({ min }) => yupMinField(t, t('Unmatched Port'), min)),
    // No .max() on the array: several points may share a port, so rows legitimately outnumber the
    // machine's ports. The cap is on distinct port numbers, in the max-ports test below.
    ports: Yup.array()
      .of(
        Yup.object().shape({
          logisticsPointId: Yup.number().required(),
          port: Yup.number().required(),
        })
      )
      .required(yupRequiredField(t, t('Ports')))
      .min(1, ({ min }) => yupMinField(t, t('Ports'), min))
      // A point maps to exactly one port (UQ_sort_map_port_per_point in the api), so a repeated
      // point is the duplicate to reject - a repeated port is now the whole point of the feature.
      .test(
        'unique-points',
        t('Each point can be mapped to only one port'),
        function (ports) {
          if (!ports) return true;

          const pointIds = ports.map((p) => p.logisticsPointId);
          return new Set(pointIds).size === pointIds.length;
        }
      )
      .test(
        'max-ports',
        t('Max ports reached'),
        function (ports) {
          if (!ports) return true;

          const portsLimit = (data && data?.maxPorts - 2) ?? 100;
          return new Set(ports.map((p) => p.port)).size <= portsLimit;
        }
      )
      .test(
        'reserved-ports',
        t('Ports must not match Error Port or Unmatched Port'),
        function (ports) {
          const { errorPort, unmatchedPort } = this.parent;
          if (!ports) return true;

          const portNumbers = ports.map((p) => p.port);
          return (
            !portNumbers.includes(errorPort) &&
            !portNumbers.includes(unmatchedPort)
          );
        }
      ),
  });
}

export function toApiData(values: CreateSortingMapsType) {
  let apiData = {
    name: values.name,
    description: values.description,
    errorPort: values.errorPort ?? 1,
    unmatchedPort: values.unmatchedPort ?? 1,
  };

  return apiData;
}

export function toApiDataPorts(ports: CreatePorts[]) {
  let apiData: CreatePorts[] = ports.map((port) => ({
    logisticsPointId: port?.logisticsPointId ?? 0,
    port: port.port ?? 1,
  }));

  return apiData;
}
